import React, { FC, useContext } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ImageDataLike } from 'gatsby-plugin-image';
import { ExperimentsContext } from '@agria/theme/src/context/ExperimentsContext';
import { WhichDogSvg } from '@agria/theme/src/components/whichDog/WhichDog';
import { WhichCatSvg } from '@agria/theme/src/components/whichCat/WhichCat';
import britishEquestrianImageSrc from '@agria/theme/src/images/logos/british-equestrian-logo.png';
import { Stack } from '../stack/Stack';
import { getHighlightColor } from '../../utils/getHighlightColor';
import { ArrowRight } from '../icon';
import { Typography } from '../typography/Typography';
import { Image } from '../Image/Image';
import { Link } from '../link/Link';
// import imageSrc from '../../images/species/dog.jpg'; // only for testing TODO: remove

// Interface

export interface SpeciesCardProps {
  image?: { image: { localFile: ImageDataLike } };
  colorCode: string;
  heading?: string;
  description?: string;
  link?: ButtonProps;
}

export interface StyledSpeciesCardProps {
  borderColor?: string;
  AGRIA108?: string;
}

export interface ButtonProps {
  label?: string;
  url: string;
  target?: string;
}

// Styles

const StyledSpeciesCard = styled.div<StyledSpeciesCardProps>`
  ${({
    theme: { space, colors, media, borderRadii },
    borderColor,
    AGRIA108,
  }) => css`
    border-radius: ${borderRadii.medium};
    background-color: ${colors.utilityWhite};
    display: flex;
    flex-direction: row;
    position: relative;
    transition: box-shadow 0.2s ease;

    ${AGRIA108 === 'variation' &&
    css`
      @media (min-width: ${media.medium}) {
        margin-top: ${space.large};
        padding-top: ${space.large};
      }
    `}

    &:hover {
      box-shadow: 4px 16px 32px -8px rgba(3, 36, 62, 0.16);
    }

    @media (min-width: ${media.medium}) {
      justify-content: flex-start;
      flex-direction: column;
    }

    ${borderColor &&
    css`
      border-left: ${space.xxSmall} solid ${colors[borderColor]};

      @media (min-width: ${media.medium}) {
        border-left: none;
        border-top: ${space.xxSmall} solid ${colors[borderColor]};
      }
    `};

    a {
      position: unset;

      &:before {
        content: '';
        position: absolute;
        display: block;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        z-index: 1;
      }
    }
  `};
`;

const StyledParentStack = styled.div`
  ${({ theme: { space, media } }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: 100%;

    @media (min-width: ${media.medium}) {
      flex-direction: column;
    }
  `};
`;

const StyledStack = styled(Stack)`
  ${({ theme: { media, space } }) => css`
    display: flex;
    justify-content: space-between;
    flex-direction: row;
    align-items: center;
    padding: ${space.small} ${space.large};

    @media (min-width: ${media.medium}) {
      flex-direction: column;
      padding: ${space.large} ${space.large} 0;
      height: 100%;

      p,
      h3 {
        text-align: center;
        width: 100%;
        margin-left: auto;
        margin-right: auto;
      }
    }
  `};
`;

const StyledDescription = styled(Typography)`
  ${({ theme: { media } }) => css`
    display: none;
    @media (min-width: ${media.medium}) {
      display: block;
    }
  `};
`;

const StyledLink = styled(Link)`
  ${({ theme: { media } }) => css`
    svg {
      display: none;
      @media (min-width: ${media.medium}) {
        display: block;
      }
    }
  `};
`;

const StyledHeading = styled(Typography)`
  ${({ theme: { media } }) => css`
    max-width: 130px;
  `};
`;

const StyledImage = styled.div`
  ${({ theme: { media } }) => css`
    height: 96px;
    flex-shrink: 0;
    width: 96px;

    .gatsby-image-wrapper {
      height: 100%;
    }

    div {
      img {
        transition: transform 0.3s ease-in-out, opacity 0.25s linear !important;
        max-height: 100%;
      }
    }

    @media (min-width: ${media.medium}) {
      height: 256px;
      width: 100%;

      ${StyledSpeciesCard}:not(:hover) & div img {
        transform: translateY(32px);
      }
    }
  `};
`;

const StyledCertification = styled.div`
  ${({ theme: { maxMedia } }) => css`
    position: absolute;
    top: 0;
    left: 50%;
    translate: -50% calc(-50% - 2px);
    width: max-content; // Prevent shrinking
    filter: drop-shadow(0 0 12px rgb(0 0 0 / 0.08));
    z-index: 1;

    @media (max-width: ${maxMedia.medium}) {
      display: none;
    }
  `};
`;

const StyledBritishEquestrianLogo = styled.div`
  ${({ theme: { colors } }) => css`
    padding: 8px;
    box-shadow: inset 0 0 0 1px ${colors.neutral100};
    border-radius: 8px;
    background-color: ${colors.utilityWhite};
  `};
`;

// JSX

export const SpeciesCard: FC<SpeciesCardProps> = ({
  image,
  colorCode,
  heading,
  description,
  link,
}) => {
  const { experiments } = useContext(ExperimentsContext);

  return (
    <StyledSpeciesCard
      borderColor={getHighlightColor(colorCode)}
      AGRIA108={experiments.AGRIA108}
    >
      <StyledParentStack>
        {experiments.AGRIA108 === 'variation' && heading && (
          <StyledCertification>
            {
              {
                dog: <WhichDogSvg width={122} height={96} />,
                cat: <WhichCatSvg width={122} height={96} />,
                rabbit: null,
                horse: (
                  <StyledBritishEquestrianLogo>
                    <img
                      src={britishEquestrianImageSrc}
                      alt="British Equestrian Official Partner"
                      width={104}
                      height={64}
                    />
                  </StyledBritishEquestrianLogo>
                ),
              }[heading.toLowerCase().replace('insurance', '').trim()]
            }
          </StyledCertification>
        )}

        <StyledStack gap="small">
          <Stack gap="xxSmall">
            <StyledHeading variant="headingMedium" component="h3">
              {heading}
            </StyledHeading>
            {description && (
              <StyledDescription variant="bodyMedium">
                {description}
              </StyledDescription>
            )}
          </Stack>
          {link && (
            <StyledLink
              label={link.label}
              aria-label={heading}
              url={link.url}
              icon={<ArrowRight size="large" />}
              iconOnRight
            />
          )}
        </StyledStack>
        {image && (
          <StyledImage>
            <Image src={image?.image?.localFile} alt="" loading="eager" />
          </StyledImage>
        )}
      </StyledParentStack>
    </StyledSpeciesCard>
  );
};

export default SpeciesCard;
